import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { FiMaximize2 } from "react-icons/fi";
import {
  inputTypeFromDataType,
  isCalculatedEntryMode,
  isBooleanDataType,
  isBooleanSwitchChecked,
  isGridDataType,
  isMultilineText,
  isMultiselectDataType,
  isNumericDataType,
  isSelectDataType,
  parseSelectKpiValue,
  serializeSelectKpiValue,
  parseGridKpiValue,
  serializeGridKpiValue,
} from "@core/reports/utils/fieldTypeUtils";
import { buildFormulaPreview } from "@features/admin/kpi/types/kpiEntryMode";
import KpiGridEditor, {
  type KpiFieldGridColumn,
  type KpiFieldGridFixedRow,
} from "@features/report/activity-entry/components/KpiGridEditor";

export type { KpiFieldGridColumn, KpiFieldGridFixedRow };

export type KpiFieldOption = {
  option_id?: number;
  kpi_option_id?: number;
  label: string;
  list_order?: number;
  allows_input?: boolean;
  is_active?: boolean;
};

export type KpiFieldOperand = {
  source_kpi_id?: number;
  kpi_id?: number;
  source_kpi_name?: string | null;
  kpi_name?: string | null;
  source_activity_id?: number | null;
  activity_id?: number | null;
  source_activity_name?: string | null;
  activity_name?: string | null;
  list_order?: number;
};

export interface KpiFieldRendererProps {
  inputId: string;
  labelId: string;
  kpiName: string;
  dataTypeName: string;
  value: string;
  required: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
  /** Unit symbol/label to show as an adornment (numeric fields only). */
  unitLabel?: string | null;
  /** Which side of the input the unit adornment sits on. */
  unitPosition?: "prefix" | "suffix";
  entryMode?: string | null;
  operation?: string | null;
  operands?: KpiFieldOperand[] | null;
  options?: KpiFieldOption[] | null;
  minSelections?: number | null;
  maxSelections?: number | null;
  gridColumns?: KpiFieldGridColumn[] | null;
  gridFixedRows?: KpiFieldGridFixedRow[] | null;
  gridMinRows?: number | null;
  gridMaxRows?: number | null;
  gridShowColumnTotals?: boolean | null;
  gridShowRowTotals?: boolean | null;
  /** Inline warning for calculated KPI calculation. */
  warningMessage?: string | null;
  /** Persist activity draft with the given grid value (modal Save). */
  onSaveDraft?: (serialized: string) => Promise<{ isSuccess?: () => boolean } | unknown>;
  /** Notify parent when the maximize modal opens/closes (autosave / Ctrl+S gate). */
  onGridModalOpenChange?: (open: boolean) => void;
  /**
   * Template preview: disabled select shows option chips; empty grid shows one blank row.
   */
  previewMode?: boolean;
}

function optionIdOf(opt: KpiFieldOption): number | null {
  const id = opt.kpi_option_id ?? opt.option_id;
  return id == null ? null : Number(id);
}

function activeOptions(options: KpiFieldOption[] | null | undefined): KpiFieldOption[] {
  return (options ?? [])
    .filter((o) => o.is_active !== false)
    .sort((a, b) => (a.list_order ?? 0) - (b.list_order ?? 0));
}

function draftSaveSucceeded(response: unknown): boolean {
  if (response && typeof response === "object" && "isSuccess" in response) {
    const fn = (response as { isSuccess?: () => boolean }).isSuccess;
    if (typeof fn === "function") return fn() === true;
  }
  return false;
}

/**
 * Renders the correct input control for a KPI based on its data type / entry mode —
 * boolean switch, select, multiselect checkboxes, grid table, calculated readonly, or
 * typed (number/date/text) control.
 */
export default function KpiFieldRenderer({
  inputId,
  labelId,
  kpiName,
  dataTypeName,
  value,
  required,
  disabled,
  onChange,
  unitLabel,
  unitPosition = "suffix",
  entryMode,
  operation,
  operands,
  options,
  gridColumns,
  gridFixedRows,
  gridMinRows,
  gridMaxRows,
  gridShowColumnTotals,
  gridShowRowTotals,
  warningMessage,
  onSaveDraft,
  onGridModalOpenChange,
  previewMode = false,
}: KpiFieldRendererProps) {
  const calculated = isCalculatedEntryMode(entryMode);
  const useBooleanSwitch = isBooleanDataType(dataTypeName);
  const useSelect = isSelectDataType(dataTypeName);
  const useMultiselect = isMultiselectDataType(dataTypeName);
  const useGrid = isGridDataType(dataTypeName);
  const useTextarea =
    !calculated &&
    !useBooleanSwitch &&
    !useSelect &&
    !useMultiselect &&
    !useGrid &&
    isMultilineText(dataTypeName);
  const inputType = inputTypeFromDataType(dataTypeName);
  const booleanChecked = isBooleanSwitchChecked(value);
  const booleanLabel = booleanChecked ? "Yes" : "No";

  const [gridModalOpen, setGridModalOpen] = useState(false);
  const [modalLocalValue, setModalLocalValue] = useState(value);
  const [modalSeedValue, setModalSeedValue] = useState(value);
  const [modalSaving, setModalSaving] = useState(false);
  const gridModalOpenRef = useRef(false);

  useEffect(() => {
    return () => {
      if (gridModalOpenRef.current) {
        onGridModalOpenChange?.(false);
        gridModalOpenRef.current = false;
      }
    };
  }, [onGridModalOpenChange]);

  const openGridModal = () => {
    setModalSeedValue(value);
    setModalLocalValue(value);
    setGridModalOpen(true);
    gridModalOpenRef.current = true;
    onGridModalOpenChange?.(true);
  };

  const closeGridModal = () => {
    setGridModalOpen(false);
    setModalSaving(false);
    gridModalOpenRef.current = false;
    onGridModalOpenChange?.(false);
  };

  const requestCloseGridModal = () => {
    if (modalSaving) return;
    if (modalLocalValue !== modalSeedValue) {
      const discard = window.confirm(
        "Discard changes to this grid? Your edits in the expanded view will be lost."
      );
      if (!discard) return;
    }
    closeGridModal();
  };

  const handleModalApply = () => {
    if (modalSaving) return;
    onChange(modalLocalValue);
    closeGridModal();
  };

  const handleModalSave = async () => {
    if (modalSaving || !onSaveDraft) return;
    setModalSaving(true);
    try {
      onChange(modalLocalValue);
      const response = await onSaveDraft(modalLocalValue);
      if (draftSaveSucceeded(response)) {
        closeGridModal();
      }
    } catch {
      // Keep modal open; draft save toasts/errors surface elsewhere.
    } finally {
      setModalSaving(false);
    }
  };

  const modalDirty = modalLocalValue !== modalSeedValue;

  const calculatedFrom = useMemo(() => {
    const ops = (operands ?? [])
      .map((op) => ({
        source_kpi_id: Number(op.source_kpi_id ?? op.kpi_id ?? 0),
        source_kpi_name: op.source_kpi_name ?? op.kpi_name ?? null,
        source_activity_id: Number(
          op.source_activity_id ?? op.activity_id ?? 0
        ),
        source_activity_name:
          op.source_activity_name ?? op.activity_name ?? null,
        list_order: op.list_order ?? 0,
      }))
      .filter((op) => op.source_kpi_id > 0)
      .sort((a, b) => a.list_order - b.list_order);
    const preview = buildFormulaPreview(operation, ops);
    if (preview) return `Calculated from ${preview}`;
    if (ops.length > 0) {
      return `Calculated from ${ops
        .map((o) => {
          const name = o.source_kpi_name || `KPI #${o.source_kpi_id}`;
          return o.source_activity_name
            ? `${name} (${o.source_activity_name})`
            : name;
        })
        .join(", ")}`;
    }
    return "Calculated";
  }, [operands, operation]);

  if (calculated) {
    return (
      <div data-testid={`${inputId}-calculated`}>
        <Form.Control
          id={inputId}
          type="text"
          className="kpi-field--calculated"
          value={value ?? ""}
          readOnly
          disabled
          aria-labelledby={labelId}
        />
        <Form.Text className="text-muted d-block mt-1">{calculatedFrom}</Form.Text>
        {warningMessage ? (
          <div className="text-warning small mt-1" role="status">
            {warningMessage}
          </div>
        ) : null}
      </div>
    );
  }

  if (useBooleanSwitch) {
    const lockInteraction = previewMode;
    return (
      <div
        className={lockInteraction ? "pointer-events-none" : undefined}
        aria-disabled={lockInteraction || undefined}
      >
        <Form.Check
          id={inputId}
          type="switch"
          className="ntcog-boolean-switch mb-0"
          checked={booleanChecked}
          label={booleanLabel}
          onChange={(e) => {
            if (lockInteraction || disabled) return;
            onChange(e.target.checked ? "true" : "false");
          }}
          disabled={lockInteraction ? false : disabled}
          aria-labelledby={labelId}
          aria-label={`${kpiName}: ${booleanLabel}`}
        />
      </div>
    );
  }

  if (useSelect || useMultiselect) {
    const parsed = parseSelectKpiValue(value);
    const opts = activeOptions(options);
    const selected = new Set(parsed.option_ids);
    const otherOpt = opts.find((o) => o.allows_input);
    const otherId = otherOpt ? optionIdOf(otherOpt) : null;

    const setSelection = (nextIds: number[]) => {
      const inputs = { ...(parsed.inputs ?? {}) };
      if (otherId != null && !nextIds.includes(otherId)) {
        delete inputs[String(otherId)];
      }
      onChange(
        serializeSelectKpiValue(
          { option_ids: nextIds, inputs, option_labels: parsed.option_labels },
          opts
        )
      );
    };

    if (useSelect) {
      const current = parsed.option_ids[0];
      return (
        <div>
          <Form.Select
            id={inputId}
            value={current == null ? "" : String(current)}
            disabled={disabled}
            required={required}
            aria-labelledby={labelId}
            onChange={(e) => {
              const id = e.target.value === "" ? null : Number(e.target.value);
              setSelection(id == null || !Number.isFinite(id) ? [] : [id]);
            }}
          >
            <option value="">{required ? "Select…" : "—"}</option>
            {opts.map((opt) => {
              const id = optionIdOf(opt);
              if (id == null) return null;
              return (
                <option key={id} value={id}>
                  {opt.label}
                </option>
              );
            })}
          </Form.Select>
          {previewMode && opts.length > 0 ? (
            <div
              className="d-flex flex-wrap gap-1 mt-2"
              aria-label="Available options"
              data-testid={`${inputId}-preview-options`}
            >
              {opts.map((opt) => {
                const id = optionIdOf(opt);
                if (id == null) return null;
                return (
                  <span
                    key={id}
                    className="badge rounded-pill bg-body-secondary text-body border fw-normal"
                  >
                    {opt.label}
                  </span>
                );
              })}
            </div>
          ) : null}
          {otherId != null && selected.has(otherId) ? (
            <Form.Control
              className="mt-2"
              type="text"
              placeholder="Please specify…"
              disabled={disabled}
              value={parsed.inputs?.[String(otherId)] ?? ""}
              onChange={(e) =>
                onChange(
                  serializeSelectKpiValue(
                    {
                      option_ids: parsed.option_ids,
                      inputs: {
                        ...(parsed.inputs ?? {}),
                        [String(otherId)]: e.target.value,
                      },
                      option_labels: parsed.option_labels,
                    },
                    opts
                  )
                )
              }
            />
          ) : null}
        </div>
      );
    }

    // MULTISELECT checkboxes (wrapping pill chips)
    const lockMultiselect = previewMode;
    const chipDisabled = lockMultiselect ? false : disabled;
    return (
      <div data-testid={`${inputId}-multiselect`}>
        <Form.Text className="text-muted d-block mb-2">
          Select all that apply.
        </Form.Text>
        <div
          className={`d-flex flex-wrap gap-2${lockMultiselect ? " pointer-events-none" : ""}`}
          role="group"
          aria-labelledby={labelId}
          aria-disabled={lockMultiselect || undefined}
        >
          {opts.map((opt) => {
            const id = optionIdOf(opt);
            if (id == null) return null;
            const active = selected.has(id);
            const checkId = `${inputId}-opt-${id}`;
            return (
              <Form.Check
                key={id}
                id={checkId}
                type="checkbox"
                inline
                className={`kpi-multiselect-option${
                  active ? " kpi-multiselect-option--selected" : ""
                }`}
                checked={active}
                label={opt.label}
                disabled={chipDisabled}
                onChange={() => {
                  if (lockMultiselect || disabled) return;
                  const next = active
                    ? parsed.option_ids.filter((x) => x !== id)
                    : [...parsed.option_ids, id];
                  setSelection(next);
                }}
              />
            );
          })}
        </div>
        {otherId != null && selected.has(otherId) ? (
          <Form.Control
            className="mt-2"
            type="text"
            placeholder="Please specify…"
            disabled={disabled}
            value={parsed.inputs?.[String(otherId)] ?? ""}
            onChange={(e) =>
              onChange(
                serializeSelectKpiValue(
                  {
                    option_ids: parsed.option_ids,
                    inputs: {
                      ...(parsed.inputs ?? {}),
                      [String(otherId)]: e.target.value,
                    },
                    option_labels: parsed.option_labels,
                  },
                  opts
                )
              )
            }
          />
        ) : null}
      </div>
    );
  }

  if (useGrid) {
    const columns = (gridColumns ?? [])
      .filter((c) => c.is_active !== false)
      .sort((a, b) => (a.list_order ?? 0) - (b.list_order ?? 0));
    const parsedGrid = parseGridKpiValue(value);
    let gridDisplayValue = value;
    if (
      previewMode &&
      (!value || parsedGrid.rows.length === 0) &&
      columns.length > 0
    ) {
      const emptyRow: Record<string, string> = { row_kind: "free" };
      for (const col of columns) {
        const key = col.column_key || "";
        if (key) emptyRow[key] = "";
      }
      gridDisplayValue = serializeGridKpiValue({ rows: [emptyRow] }, columns);
    }

    const gridEditorProps = {
      inputId,
      disabled,
      gridColumns,
      gridFixedRows,
      gridMinRows,
      gridMaxRows,
      gridShowColumnTotals,
      gridShowRowTotals,
    };

    return (
      <div>
        {!disabled ? (
          <div className="d-flex justify-content-end mb-2">
            <Button
              type="button"
              variant="outline-secondary"
              size="sm"
              className="ntcog-table-action-btn"
              onClick={openGridModal}
              aria-label="Expand grid"
              title="Expand grid"
              data-testid={`${inputId}-grid-maximize`}
            >
              <FiMaximize2 />
            </Button>
          </div>
        ) : null}

        <KpiGridEditor
          {...gridEditorProps}
          value={gridDisplayValue}
          onChange={onChange}
        />

        <Modal
          show={gridModalOpen}
          onHide={requestCloseGridModal}
          size="xl"
          fullscreen="lg-down"
          backdrop={modalSaving ? "static" : true}
          keyboard={!modalSaving}
          data-testid={`${inputId}-grid-modal`}
        >
          <Modal.Header closeButton={!modalSaving} className="border-0 pb-0">
            <Modal.Title className="fw-semibold">{kpiName}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="pt-3" style={{ overflow: "auto" }}>
            <KpiGridEditor
              {...gridEditorProps}
              inputId={`${inputId}-modal`}
              value={modalLocalValue}
              onChange={setModalLocalValue}
              disabled={modalSaving}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={requestCloseGridModal}
              disabled={modalSaving}
              data-testid={`${inputId}-grid-modal-cancel`}
            >
              Cancel
            </Button>
            <Button
              variant="outline-primary"
              onClick={handleModalApply}
              disabled={modalSaving || !modalDirty}
              data-testid={`${inputId}-grid-modal-apply`}
            >
              Apply
            </Button>
            {onSaveDraft ? (
              <Button
                variant="primary"
                onClick={() => void handleModalSave()}
                disabled={modalSaving || !modalDirty}
                aria-busy={modalSaving}
                data-testid={`${inputId}-grid-modal-save`}
              >
                {modalSaving ? (
                  <>
                    <Spinner
                      animation="border"
                      size="sm"
                      className="me-2"
                      aria-hidden
                    />
                    Saving…
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            ) : null}
          </Modal.Footer>
        </Modal>
      </div>
    );
  }

  if (useTextarea) {
    return (
      <Form.Control
        id={inputId}
        as="textarea"
        rows={3}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={required ? "Required..." : "Enter value..."}
        required={required}
        disabled={disabled}
        aria-labelledby={labelId}
      />
    );
  }

  const control = (
    <Form.Control
      id={inputId}
      type={inputType}
      inputMode={inputType === "number" ? "decimal" : undefined}
      step={inputType === "number" ? "any" : undefined}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={required ? "Required..." : "Enter value..."}
      required={required}
      disabled={disabled}
      aria-labelledby={labelId}
    />
  );

  const showUnit = inputType === "number" && !!unitLabel && isNumericDataType(dataTypeName);
  if (!showUnit) return control;

  return (
    <div className="input-group flex-nowrap">
      {unitPosition === "prefix" ? (
        <span className="input-group-text text-nowrap bg-body-secondary text-muted">
          {unitLabel}
        </span>
      ) : null}
      {control}
      {unitPosition === "suffix" ? (
        <span className="input-group-text text-nowrap bg-body-secondary text-muted">
          {unitLabel}
        </span>
      ) : null}
    </div>
  );
}
